import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import { Heart, MapPin, Send, CheckCircle2, ShieldCheck, PawPrint } from "lucide-react";

export default function FoundPet() {
  const { petId } = useParams();
  const [pet, setPet] = useState(null);
  const [form, setForm] = useState({ finder_name: "", finder_contact: "", finder_location: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/pets/${petId}`).then(r => setPet(r.data)).catch(() => setPet({ error: true }));
  }, [petId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/public/pets/notify-owner`, { pet_id: petId, ...form });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
  };

  if (!pet) return <div className="min-h-screen grid place-items-center text-ink-soft">Loading…</div>;
  if (pet.error) return (
    <div className="min-h-screen grid place-items-center px-5 text-center">
      <div>
        <PawPrint className="w-12 h-12 text-brand mx-auto"/>
        <h1 className="font-heading text-2xl font-extrabold mt-3">Pet not found</h1>
        <p className="text-ink-soft mt-2">This QR code may be inactive. Visit <Link to="/" className="text-brand font-bold underline">PetBond</Link>.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg" data-testid="found-pet-page">
      <header className="border-b border-line bg-surface">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/"><img src="/brand/petbond-logo.png" alt="PetBond" className="h-10"/></Link>
          <span className="text-xs font-bold uppercase tracking-wider text-ink-soft inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Verified Pet ID</span>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-5 py-8">
        <div className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
          {pet.photo && <img src={pet.photo} alt={pet.name} className="w-full h-64 object-cover"/>}
          <div className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1.5">
              {pet.is_lost ? <Heart className="w-3 h-3 fill-brand"/> : <PawPrint className="w-3 h-3"/>}
              {pet.is_lost ? "Lost · Help reunite" : "Pet ID Card"}
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-2">Hello, I'm {pet.name}!</h1>
            <p className="text-ink-soft text-sm mt-1">A {pet.breed} {pet.species}{pet.city ? ` from ${pet.city}` : ""}.</p>
            {pet.is_lost && (
              <div className="mt-4 bg-brand/10 border border-brand/30 rounded-2xl p-4 text-sm">
                <b>I'm lost!</b> If you've found me, please notify {pet.owner_first_name} below. They'll be SO relieved 💛
              </div>
            )}
          </div>
        </div>

        {submitted ? (
          <div className="mt-6 bg-leaf/40 border border-leaf rounded-3xl p-6 text-center" data-testid="notify-success">
            <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto"/>
            <h2 className="font-heading text-2xl font-extrabold mt-3">Thank you! 🐾</h2>
            <p className="text-ink-soft mt-2">The owner has been notified instantly. Please stay safe with {pet.name} — they'll reach out as soon as they see your message.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 bg-surface border border-line rounded-3xl p-6 space-y-4" data-testid="notify-owner-form">
            <h2 className="font-heading text-xl font-extrabold">Notify the owner</h2>
            <p className="text-xs text-ink-soft -mt-2">Your details go ONLY to the owner — we don't share their phone with you for privacy.</p>
            <Field label="Your name *"><input data-testid="finder-name" required value={form.finder_name} onChange={e => setForm({...form, finder_name: e.target.value})} className="inp"/></Field>
            <Field label="Your phone or email *"><input data-testid="finder-contact" required value={form.finder_contact} onChange={e => setForm({...form, finder_contact: e.target.value})} placeholder="+91-… or you@example.com" className="inp"/></Field>
            <Field label="Where did you find them?"><input data-testid="finder-location" value={form.finder_location} onChange={e => setForm({...form, finder_location: e.target.value})} placeholder="Park / street / landmark" className="inp"/></Field>
            <Field label="Message (optional)"><textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3} placeholder="They're safe with me, friendly, etc." className="inp"/></Field>
            {error && <p className="text-sm text-brand">{error}</p>}
            <button data-testid="notify-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-brand-hover"><Send className="w-4 h-4"/> Notify owner now</button>
          </form>
        )}

        <p className="text-center text-xs text-ink-soft mt-6">
          Built with ♥ by <Link to="/" className="font-bold text-ink hover:text-brand">PetBond</Link> · www.petbond.in
        </p>
      </main>
      <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.6rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
